import type { ChatForm as ChatFormValues } from '../../../schema/spark/chat-form-schema';
import { ChatApi } from '../../api/chat-api';
import type {
  ChatAttachment,
  ChatFailedUpload,
  ChatMessageEntry,
} from '../../types/chat.types';
import { ChatUploadUtil } from '../../utils/chat-upload-util';
import { ChatMessageOriginators } from '../../enums/chat-message-originators';

import { SparkSendRecoveryUtil } from './spark-send-recovery-util';
import type { SparkSession } from './spark-session.types';

/**
 * SparkSendTaskUtil - I run one prompt's send from end to end. ONLY concerned with ordering the
 * wire calls for a single prompt and applying what they answer with - the queue decides when I
 * run, and `ChatUploadUtil` is what actually puts the files on the server.
 */
export const SparkSendTaskUtil = {
  async runSend({
    attachments,
    chat,
    entry,
    prompt,
    session,
  }: {
    attachments: ChatAttachment[];
    chat: ChatFormValues['chat'];
    entry: ChatMessageEntry;
    prompt: string;
    session: SparkSession;
  }) {
    const { queue, transcript } = session;

    const chatId = transcript.activeChatIdRef.current;
    /*
      Captured before the send, compared after it. Everything below this that describes what is on
      screen is only applied if the reader is still looking at the screen the prompt was sent from.
    */
    const generation = transcript.screenGenerationRef.current;

    queue.unstagePrompt(entry.id);
    transcript.setChatLog(previous => [...previous, entry]);
    transcript.setPinnedPromptId(entry.id);

    const startTime = Date.now();

    let sendChatId = chatId;

    /*
      What the recovery hands back if this fails: the same attachments, with the ones that reached
      the server carrying their ids so a retry does not upload them again.
    */
    let stamped = attachments;

    /** Which file the server would not take, and how many times it has now refused it. */
    let failedUpload: ChatFailedUpload | null = null;

    try {
      const upload = await ChatUploadUtil.uploadAll({
        attachments,
        chatId,
        mintChat: mintedChatId => {
          transcript.activeChatIdRef.current = mintedChatId;
        },
      });

      sendChatId = upload.chatId;
      stamped = upload.stamped;
      failedUpload = upload.failedUpload;

      if (upload.error !== null) throw upload.error;

      const uploadedFileIds = upload.fileIds;

      /*
        The ids the turn on screen was drawn without: it was minted from local `File`s before any
        upload answered, so this is what makes its attachments fetchable and downloadable. The names
        stay as they were - the local one is what the reader recognises.
      */
      if (uploadedFileIds.length > 0) {
        // A local, because TypeScript drops the narrowing the moment it is read inside a callback.
        const uploadedChatId = sendChatId ?? undefined;

        transcript.setChatLog(previous =>
          previous.map(logEntry =>
            logEntry.id === entry.id
              ? {
                  attachments: entry.attachments?.map((file, index) => {
                    const uploaded = stamped[index]?.uploaded;

                    return {
                      fileId:
                        uploaded?.chatId === uploadedChatId
                          ? uploaded?.fileId
                          : undefined,
                      fileName: file.fileName,
                    };
                  }),
                  // Both ids, because fetching the bytes back needs both.
                  chatId: uploadedChatId,
                  id: entry.id,
                  matchingCandidates: entry.matchingCandidates,
                  messageOriginator: entry.messageOriginator,
                  text: entry.text,
                }
              : logEntry
          )
        );
      }

      const { data } = await ChatApi.sendChat({
        /*
          Null here starts a conversation and an id continues one - one call either way, because it
          is one route. The server's own test is whether the body carries an `id`.
        */
        chatId: sendChatId,
        fileIds: uploadedFileIds,
        // Read now, not captured at submit: a prompt that waited must quote the answer that arrived while it waited.
        lastKnownMessageId: sendChatId
          ? transcript.lastAnswerIdRef.current
          : null,
        prompt,
      });

      // Left the screen this was sent from: the send stands, the paint does not.
      /*
        Before the generation check: with no attachment nothing minted during the upload, and the
        conversation exists whether or not its answer is still wanted on screen.
      */
      sendChatId = data.id;

      if (transcript.screenGenerationRef.current !== generation) return;

      if (data.message?.id) {
        transcript.lastAnswerIdRef.current = data.message.id;
      }

      if (!chatId) {
        transcript.loadedChatIdRef.current = data.id;
        // The ref first: the next queued send is dequeued before React re-renders with the new id.
        transcript.activeChatIdRef.current = data.id;
        transcript.setActiveChatId(data.id);
      }

      transcript.setChatLog(previous => [
        ...previous,
        {
          // Keyed like every other entry - Chat.tsx renders the log off `entry.id`, so a meta
          // entry without one takes `key={undefined}`.
          id: crypto.randomUUID(),
          runTimeMs: Date.now() - startTime,
          messageOriginator: ChatMessageOriginators.METADATA,
        },
        {
          id: crypto.randomUUID(),
          matchingCandidates: data.message?.matchingCandidates?.length
            ? data.message.matchingCandidates
            : undefined,
          messageOriginator: ChatMessageOriginators.LLM,
          text: data.message?.text ?? '',
        },
      ]);
      transcript.showMatchingCandidates(data.message?.matchingCandidates ?? []);
    } catch (error) {
      await SparkSendRecoveryUtil.recoverFromSendFailure({
        // Stamped, so a retry uploads only what has not landed yet.
        attachments: stamped,
        failedUpload,
        chat,
        chatId,
        entry,
        error,
        generation,
        session,
      });
    } finally {
      /*
        **In `finally`, because the conversation exists either way.** A send that minted one and then
        failed used to leave it absent from the sidebar for the rest of the session: the refresh sat
        after `sendChat`, and the retry saw a non-null id and skipped it too.
      */
      if (!chatId && sendChatId) await ChatApi.refreshChatHistory();
    }
  },
};
